import React, { useContext, useState, useEffect } from 'react';
import { View, Text } from 'react-native';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import moment from 'moment';

import DataState from 'app/src/components/DataState';
import SellerAssets from 'app/src/components/Assets/SellerAssets';
import CardIndex from 'app/src/components/Users/BuyerUsers/CardIndex';
import AuthContext from 'app/src/contexts/AuthContext';
import Modal from 'app/src/elements/Modal';
import { elRed } from 'app/src/styles/colors';
import { Button } from 'app/src/elements/buttons';
import { Warning, WarningText } from 'app/src/styles';
import { formatAddress } from 'app/src/utils/formatAssetName';
import EntityAttachments from '../Attachments/EntityAttachments';
import BankAccounts from '../BankAccounts';
import secrets from '../../../secrets';

import Review from './Review';
import TaxExemption from './TaxExemption';
import { BUYER, SEND_DEFAULT_TITLE_RELEASE_INVITE } from './queries';
import {
  BuyersContainer,
  BuyersShowContainer,
  BuyerAllAssetsContainer,
  TabButtonContainer,
  TabButton,
  SectionCard,
  SectionHeader
} from './elements';
import { BankAccountsContainer } from '../../elements';

const FILE_TYPES = [
  {
    name: "W9",
    required: false,
  },
  {
    name: "Other",
    required: false
  }
];

const ReviewDetails = ({ buyer }) => {
  if (!buyer.reviewedAt) { return null; }

  const action = 'Active' === buyer.status ? 'Approved' : buyer.status;

  return (
    <View>
      <Text>{action} by {buyer.reviewedBy?.fullName}</Text>
      <Text>
        {moment(buyer.reviewedAt).format('MM-DD-YY \@ hh:mmA')}
      </Text>
      <Text>Review Notes: {buyer.reviewNotes}</Text>
    </View>
  );
};

const Buyer = () => {
  const { buyerId } = useParams();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const { currentUser } = useContext(AuthContext);

  const defaultFileIds = {}
  FILE_TYPES.forEach(f => defaultFileIds[f.name] = []);
  const [fileIds, setFileIds] = useState(defaultFileIds);

  const { loading, data, refetch } = useQuery(
    BUYER, { variables: { id: buyerId } }
  );

  useEffect(() => {
    if(data?.buyer) {
      let keys = Object.keys(fileIds);
      let newFileIds = { ...fileIds };
      keys?.forEach(key => {
        newFileIds[key] = data.buyer.files?.filter(
          file => file.name === key
        );
      });
      setFileIds(newFileIds);
    }
  }, [data]);

  const [sendInvite] = useMutation(SEND_DEFAULT_TITLE_RELEASE_INVITE, {
    onCompleted: () => refetch(),
  });

  if (loading || !data) { return <DataState.Loading />; }

  const buyer = data.buyer;
  const hasAdminBuyerUser = buyer.buyerUsers?.some(u => u.role === 'Admin');

  let statusInformation = null;

  if ('New' === buyer.status) {
    statusInformation = (
      <Warning>
        <WarningText>
          This buyer was created via the website. The user has not yet
          entered billing contact information.
        </WarningText>
      </Warning>
    );
  } else if ('Review' === buyer.status) {
    statusInformation = (
      <Warning>
        <WarningText>
          This buyer was created via the website. The user has entered billing
          contact information and can be approved for bidding.
        </WarningText>
        {currentUser?.canApproveBuyer && (
          <>
            <Button
              label="Approve for Bidding"
              style={{ width: 300, backgroundColor: 'green' }}
              onPress={() => setShowReviewModal('Approve')}
            />
            <Button
              label="Decline access to Bidding"
              style={{ width: 300 }}
              onPress={() => setShowReviewModal('Decline')}
            />
          </>
        )}
      </Warning>
    );
  } else if ('Declined' === buyer.status) {
    statusInformation = (
      <Warning>
        <WarningText>
          This buyer was previously declined access to bidding.
        </WarningText>
        <ReviewDetails buyer={buyer} />
        {currentUser?.canApproveBuyer && (
          <Button
            label="Change to Approve"
            onPress={() => setShowReviewModal('Approve')}
          />
        )}
      </Warning>
    );
  }

  return (
    <BuyersContainer>
      <BuyersShowContainer>
        <SectionCard>
          <SectionHeader>Buyer Details</SectionHeader>
          {statusInformation}
          <Text>{buyer.name}</Text>
          <Text>{buyer.companyName}</Text>
          <Text>{formatAddress(buyer, 'Buyer')}</Text>
          <Text>{buyer.email}</Text>
          <Text>{buyer.phoneNumber?.formatted}</Text>
          {buyer.registeredAt && (
            <Text>
              Registered on Website:
              {moment(buyer.registeredAt).format(' MM-DD-YY')}
            </Text>
          )}
          {buyer.publicAuctionBuyer === true &&
            <Text style={{fontWeight: 'bold'}}>Public Auction Buyer</Text>
          }
          {'Approved' === buyer.status && (
            <View style={{marginTop: 20}}>
              <Text>Buyer is approved for online bidding.</Text>
              <ReviewDetails buyer={buyer} />
              {currentUser?.canApproveBuyer && (
                <Button
                  label="Change to Declined"
                  onPress={() => setShowReviewModal('Decline')}
                />
              )}
            </View>
          )}
          {'Active' === buyer.status && 0 === buyer.buyerUsers.length &&
            currentUser?.canApproveBuyer && (
              <View style={{marginTop: 20}}>
                <Text>Buyer has not applied for bidding and has no online users.</Text>
                <Button
                  label="Decline Future Requests"
                  onPress={() => setShowReviewModal('Decline')}
                />
              </View>
          )}
          {'Active' === buyer.status && buyer.buyerUsers.length > 0 &&
            currentUser?.canApproveBuyer && (
              <View style={{marginTop: 20}}>
                <Text>Buyer User was added through auction portal.</Text>
                <Button
                  label="Approve for Bidding"
                  style={{ width: 300, backgroundColor: 'green' }}
                  onPress={() => setShowReviewModal('Approve')}
                />
                <Button
                  label="Decline access to Bidding"
                  style={{ width: 300 }}
                  onPress={() => setShowReviewModal('Decline')}
                />
              </View>
          )}
        </SectionCard>

        <TaxExemption buyer={buyer} refetch={refetch} />

        { secrets.app.doubleEntry &&
          <>
            <SectionCard>
              <SectionHeader>Attachments</SectionHeader>
              <EntityAttachments
                ownerType={'Buyer'}
                ownerId={buyer?.id}
                fileTypes={FILE_TYPES}
                fileIds={fileIds}
                setFileIds={setFileIds}
              />
            </SectionCard>


            <SectionCard>
              <SectionHeader>Default Title Release</SectionHeader>

              {!hasAdminBuyerUser && (
                <Text>
                  At least one buyer user must be set to "Admin" before you can invite the buyer.
                </Text>
              )}

              {hasAdminBuyerUser && (
                <>
                  {(!buyer.defaultTitleReleaseInviteAt && !buyer.defaultTitleReleaseAt) && (
                    <>
                      <Text>No invite sent yet.</Text>
                      <Button
                        label="Send Invite Email"
                        onPress={() => sendInvite({ variables: { id: buyer.id } })}
                      />
                    </>
                  )}

                  {(buyer.defaultTitleReleaseInviteAt && !buyer.defaultTitleReleaseAt) && (
                    <>
                      <Text>Invite sent on {moment(buyer.defaultTitleReleaseInviteAt).format('MM-DD-YY @ hh:mmA')}</Text>
                      <Button
                        label="Resend Invite Email"
                        onPress={() => sendInvite({ variables: { id: buyer.id } })}
                      />
                    </>
                  )}

                  {(buyer.defaultTitleReleaseInviteAt && buyer.defaultTitleReleaseAt) && (
                    <View>
                      <Text>Default Title Release completed at {moment(buyer.defaultTitleReleaseAt).format('MM-DD-YY @ hh:mmA')}</Text>

                      <Text style={{ marginTop: 10, fontWeight: 'bold' }}>Information On Title:</Text>
                      <Text>{buyer.titleOwnerName || '—'}</Text>
                      <Text>{buyer.titleShippingAddress1 || '—'}</Text>
                      <Text>{buyer.titleShippingAddress2 || ''}</Text>
                      <Text>{buyer.titleCity || ''}, {buyer.titleState || ''} {buyer.titleZip || ''}</Text>
                      <br />
                      <Text style={{ marginTop: 10, fontWeight: 'bold' }}>Default Shipping Information:</Text>
                      <Text>{buyer.titleShipOwnerName || '—'}</Text>
                      <Text>{buyer.titleShipShippingAddress1 || '—'}</Text>
                      <Text>{buyer.titleShipShippingAddress2 || ''}</Text>
                      <Text>{buyer.titleShipCity || ''}, {buyer.titleShipState || ''} {buyer.titleShipZip || ''}</Text>

                      {buyer.defaultTitleReleaseSignature && (
                        <View style={{ marginTop: 20 }}>
                          <Text style={{ fontWeight: 'bold', marginBottom: 5 }}>Digital Signature:</Text>
                          <img
                            src={buyer.defaultTitleReleaseSignature}
                            alt="Buyer Signature"
                            style={{
                              border: '1px solid #ccc',
                              width: '300px',
                              height: '100px',
                              objectFit: 'contain'
                            }}
                          />
                        </View>
                      )}
                    </View>
                  )}
                </>
              )}
            </SectionCard>


          </>
        }

        <SectionCard backgroundColor={'#f5f5f5'}>
          <SectionHeader>Buyer Users</SectionHeader>
          <TabButtonContainer>
            <TabButton style={{backgroundColor: elRed }}>
              <Text>Users</Text>
            </TabButton>
          </TabButtonContainer>
          <CardIndex
            buyerUsers={buyer.buyerUsers}
            buyerId={buyerId}
            refetch={refetch}
          />
        </SectionCard>
      </BuyersShowContainer>

      <BuyerAllAssetsContainer>
        <SellerAssets withNav={false} buyerId={buyerId} />
      </BuyerAllAssetsContainer>

      { secrets.app.doubleEntry &&
        <SectionCard>
          <SectionHeader>Bank Accounts</SectionHeader>
          <BankAccounts ownerType='Buyer' ownerId={buyerId} />
        </SectionCard>
      }

      {showReviewModal &&
        <Modal
          showModal={showReviewModal}
          setShowModal={setShowReviewModal}
          modalType={`${showReviewModal} ${buyer.name}`}
        >
          <Review
            type={showReviewModal}
            buyer={buyer}
            refetch={refetch}
            setShowModal={setShowReviewModal}
          />
        </Modal>
      }
    </BuyersContainer>
  );
};

export default Buyer;
